import { useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Grid,
  Group,
  Paper,
  ScrollArea,
  SimpleGrid,
  Skeleton,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Tooltip,
} from '@mantine/core';
import { IconCoin, IconListNumbers, IconReceipt2, IconWallet } from '@tabler/icons-react';
import type { ListSummary, ListSummaryBucket, TrxStatus } from '../api/types';
import { formatIdr, statusColor } from '../lib/format';
import { useTranslation } from '../i18n/LocaleContext';

const STATUS_ORDER: TrxStatus[] = ['paid', 'pending', 'failed'];
const TOP_N = 6;

function pct(part: number, total: number): number {
  if (!total) return 0;
  return Math.round((Math.abs(part) / Math.abs(total)) * 1000) / 10;
}

/**
 * Overview of the CURRENTLY FILTERED rows, above the list filters:
 *
 *   ┌ KPI ─────┬ KPI ─────┬ KPI ─────┬ KPI ─────┐   count · amount · fee · merchant revenue
 *   ┌ By status ───────────┐ ┌ By method / bank ──────────────┐
 *   │ stacked share bar    │ │ ranked rows with share bars     │
 *   │ status·count·amount·%│ │ top 6, "show all (N)" expands   │
 *
 * Backed by `GET /api/{deposits,disbursements}/summary`, which applies the
 * exact same filter set as the list page. Amount shares are relative to the
 * filtered total amount.
 */
export function ListSummaryStrip({
  summary,
  loading,
  error,
}: {
  summary: ListSummary | undefined;
  loading: boolean;
  error: unknown;
}) {
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState(false);
  const totals = summary?.totals;
  const totalAmount = totals?.amount ?? 0;
  const byStatus = new Map((summary?.byStatus ?? []).map((r) => [r.status, r]));
  const groups = summary?.byMethod ?? [];
  const visibleGroups = showAll ? groups : groups.slice(0, TOP_N);

  const tiles = [
    { key: 'count', label: t('listSummary.total'), value: totals ? totals.count.toLocaleString('id-ID') : null, icon: IconListNumbers, color: 'brand' },
    { key: 'amount', label: t('listSummary.amount'), value: totals ? formatIdr(totals.amount) : null, icon: IconCoin, color: 'teal' },
    { key: 'fee', label: t('listSummary.fee'), value: totals ? formatIdr(totals.fee) : null, icon: IconReceipt2, color: 'grape' },
    { key: 'revenue', label: t('listSummary.revenue'), value: totals ? formatIdr(totals.merchantRevenue) : null, icon: IconWallet, color: 'orange' },
  ];

  return (
    <Card>
      <Stack gap="md">
        <Group justify="space-between">
          <Text size="sm" fw={600}>
            {t('listSummary.title')}
          </Text>
          <Text size="xs" c="dimmed">
            {error ? t('listSummary.loadFailed') : t('listSummary.filteredHint')}
          </Text>
        </Group>

        <SimpleGrid cols={{ base: 2, md: 4 }} spacing="sm">
          {tiles.map((k) => (
            <Paper key={k.key} withBorder p="sm" radius="md">
              <Group gap="sm" wrap="nowrap" align="center">
                <ThemeIcon color={k.color} variant="light" size={38} radius="md">
                  <k.icon size={20} />
                </ThemeIcon>
                <Stack gap={2} style={{ minWidth: 0 }}>
                  <Text c="dimmed" size="xs" tt="uppercase" fw={600} lineClamp={1}>
                    {k.label}
                  </Text>
                  {k.value === null || loading ? (
                    <Skeleton height={22} width={120} />
                  ) : (
                    <Text size="lg" fw={700} lineClamp={1} style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {k.value}
                    </Text>
                  )}
                </Stack>
              </Group>
            </Paper>
          ))}
        </SimpleGrid>

        <Grid gutter="sm">
          {/* ---- by status ---- */}
          <Grid.Col span={{ base: 12, md: 5 }}>
            <Paper withBorder p="sm" radius="md" h="100%">
              <Stack gap="xs">
                <Text size="xs" c="dimmed" fw={600} tt="uppercase">
                  {t('listSummary.byStatus')}
                </Text>
                {/* stacked share bar (amount) */}
                <Group gap={2} wrap="nowrap" style={{ height: 8, borderRadius: 4, overflow: 'hidden' }}>
                  {STATUS_ORDER.map((s) => {
                    const r = byStatus.get(s);
                    const w = pct(r?.amount ?? 0, totalAmount);
                    return w > 0 ? (
                      <Tooltip key={s} label={`${s} · ${w}%`} withArrow>
                        <div style={{ width: `${w}%`, height: '100%', background: `var(--mantine-color-${statusColor(s)}-6)` }} />
                      </Tooltip>
                    ) : null;
                  })}
                  {totalAmount === 0 && (
                    <div style={{ width: '100%', height: '100%', background: 'var(--mantine-color-default-border)' }} />
                  )}
                </Group>
                <Table verticalSpacing={4} horizontalSpacing="xs" withRowBorders={false} fz="xs">
                  <Table.Tbody>
                    {STATUS_ORDER.map((s) => {
                      const r: ListSummaryBucket | undefined = byStatus.get(s);
                      return (
                        <Table.Tr key={s}>
                          <Table.Td>
                            <Badge color={statusColor(s)} variant="light" size="sm" radius="sm" style={{ textTransform: 'none' }}>
                              {s}
                            </Badge>
                          </Table.Td>
                          <Table.Td ta="right" style={{ fontVariantNumeric: 'tabular-nums' }}>
                            {(r?.count ?? 0).toLocaleString('id-ID')} {t('listSummary.count')}
                          </Table.Td>
                          <Table.Td ta="right" fw={600} style={{ fontVariantNumeric: 'tabular-nums' }}>
                            {formatIdr(r?.amount ?? 0)}
                          </Table.Td>
                          <Table.Td ta="right" c="dimmed" style={{ fontVariantNumeric: 'tabular-nums', width: 56 }}>
                            {pct(r?.amount ?? 0, totalAmount)}%
                          </Table.Td>
                        </Table.Tr>
                      );
                    })}
                  </Table.Tbody>
                </Table>
              </Stack>
            </Paper>
          </Grid.Col>

          {/* ---- by method / bank (ranked) ---- */}
          <Grid.Col span={{ base: 12, md: 7 }}>
            <Paper withBorder p="sm" radius="md" h="100%">
              <Stack gap="xs">
                <Group justify="space-between">
                  <Group gap={6}>
                    <Text size="xs" c="dimmed" fw={600} tt="uppercase">
                      {summary?.dimension === 'bank' ? t('listSummary.byBank') : t('listSummary.byMethod')}
                    </Text>
                    {groups.length > 0 && (
                      <Badge size="xs" variant="default" radius="sm">
                        {t('listSummary.groups', { n: groups.length })}
                      </Badge>
                    )}
                  </Group>
                  {groups.length > TOP_N && (
                    <Button size="compact-xs" variant="subtle" onClick={() => setShowAll((v) => !v)}>
                      {showAll ? t('listSummary.showTop', { n: TOP_N }) : t('listSummary.showAll', { n: groups.length })}
                    </Button>
                  )}
                </Group>
                {groups.length === 0 && !loading && (
                  <Text size="xs" c="dimmed">
                    —
                  </Text>
                )}
                <ScrollArea.Autosize mah={showAll ? 300 : undefined} type="auto" offsetScrollbars>
                  <Table verticalSpacing={4} horizontalSpacing="xs" withRowBorders={false} fz="xs">
                    <Table.Tbody>
                      {visibleGroups.map((m, i) => {
                        const share = pct(m.amount, totalAmount);
                        return (
                          <Table.Tr key={m.key}>
                            <Table.Td c="dimmed" style={{ width: 28, fontVariantNumeric: 'tabular-nums' }}>
                              {i + 1}
                            </Table.Td>
                            <Table.Td style={{ minWidth: 0 }}>
                              <Tooltip label={`${t('listSummary.fee')}: ${formatIdr(m.fee)} · ${t('listSummary.revenue')}: ${formatIdr(m.merchantRevenue)}`} withArrow>
                                <div>
                                  <Text size="xs" fw={600} lineClamp={1}>
                                    {m.key}
                                  </Text>
                                  <div style={{ height: 5, borderRadius: 3, background: 'var(--mantine-color-default-border)', marginTop: 3 }}>
                                    <div style={{ width: `${share}%`, height: '100%', borderRadius: 3, background: 'var(--mantine-primary-color-filled)' }} />
                                  </div>
                                </div>
                              </Tooltip>
                            </Table.Td>
                            <Table.Td ta="right" style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                              {m.count.toLocaleString('id-ID')} {t('listSummary.count')}
                            </Table.Td>
                            <Table.Td ta="right" fw={600} style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                              {formatIdr(m.amount)}
                            </Table.Td>
                            <Table.Td ta="right" c="dimmed" style={{ width: 56, fontVariantNumeric: 'tabular-nums' }}>
                              {share}%
                            </Table.Td>
                          </Table.Tr>
                        );
                      })}
                    </Table.Tbody>
                  </Table>
                </ScrollArea.Autosize>
              </Stack>
            </Paper>
          </Grid.Col>
        </Grid>
      </Stack>
    </Card>
  );
}
